/**
 * Every sentence of the film state of the ad's page (docs/SPEC.md §7.5, §7.6,
 * §8, §10, §14.1): the scrubber, looking around and pointing, the box, the
 * versions — and the copy table of a change's codes (§8.6). The server sends
 * codes and parameters, never prose, except the model's `say`, a question's
 * words, and `error_params.sentence` (the sentence our code wrote for a
 * cannot whose code this table lacks).
 *
 * The codes' English is copied from `backend/film/pipeline/events.py::SENTENCES` and
 * `SCOPE_SENTENCES` and from the say and cannot codes of `backend/film/ops.py`
 * (P22-T01, docs/DECISIONS.md 2026-10-14 P22-T01 (15));
 * `tests/backend/film/test_changes_copy.py` keeps the first two equal.
 */
import { ApiError } from "../api/client";
import type { Change, Said } from "../api/changes";
import { sentenceFor } from "./errors";
import { isPlain } from "./plain";

/** `{name}` → the parameter; numbers as people write them, a list with
 *  commas and "and" before the last ("2, 3 and 4"). A sentence with
 *  a parameter it lacks is not shown (`null`). */
export function fillAll(
  sentence: string,
  params: Record<string, unknown> = {},
): string | null {
  let missing = false;
  const out = sentence.replace(/\{(\w+)\}/g, (_whole, key: string) => {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      missing = true;
      return "";
    }
    if (Array.isArray(value)) {
      if (value.length === 0) missing = true;
      const items = value.map(String);
      return items.length > 1
        ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
        : items.join("");
    }
    if (typeof value === "number") {
      return Number.isInteger(value)
        ? value.toLocaleString("en-US")
        : String(Math.round(value * 10) / 10);
    }
    return String(value);
  });
  return missing ? null : out;
}

// ------------------------------------------------------------ the codes (§8.6)

/** `pipeline/events.py::SCOPE_SENTENCES`: the line after every `say` (§8.3). */
export const SCOPE_SENTENCES: Record<string, string> = {
  scope_object:
    "— on {object} in scene {scene}. Say 'everywhere' for every scene.",
  scope_object_everywhere:
    "— on {object} in every scene. Say 'only here' for scene {scene} alone.",
  scope_moment:
    "— at this moment of scene {scene}. Say 'the whole scene' for all of it.",
  scope_span:
    "— in the marked part of scene {scene}. Say 'the whole scene' for all of it.",
  scope_scene: "— in scene {scene}. Say 'everywhere' for all scenes.",
  scope_film: "— in every scene. Say 'only here' for scene {scene} alone.",
};

/** The scope lines when the scene or the object has no name to give. */
const SCOPE_PLAIN: Record<string, string> = {
  scope_object: "— on this object. Say 'everywhere' for every scene.",
  scope_object_everywhere: "— on this object in every scene.",
  scope_moment: "— at this moment. Say 'the whole scene' for all of it.",
  scope_span: "— in the marked part. Say 'the whole scene' for all of it.",
  scope_scene: "— in this scene. Say 'everywhere' for all scenes.",
  scope_film: "— in every scene.",
};

/** `pipeline/events.py::SENTENCES`: a change's own codes. */
export const SENTENCES: Record<string, string> = {
  design_confirm:
    "Designing the object and fitting it into the scene costs about {fee} credits. Say yes.",
  design_unavailable: "Object design is unavailable just now. Ask again later.",
  design_working:
    "Designing the object to fit this scene; it will be added when ready.",
  design_failed:
    "The object could not be made. Your scene is unchanged; you can try again.",
  design_stale:
    "The scene changed while the object was being made. Your object is in Objects; ask to use it here.",
  design_uneditable:
    "This object has no editable source. Ask to create a new object for this scene instead.",
  change_failed: "I could not make that change. Try saying it differently.",
  change_unavailable:
    "The change could not be made just now. Ask again in a moment.",
  change_rejected:
    "The change could not be made just now. Ask again in a moment.",
  change_refused: "I cannot make that change.",
  no_change: "Nothing was changed.",
  unchanged: "The film already looks like that.",
  no_version: "There is no film to change yet.",
  unknown_version: "There is no version {version}.",
  version_not_ready: "Version {version} never played; choose another one.",
  restored: "Back to version {version}.",
  template_card: "Saved as the template {name}. Name the parts that change.",
  template_failed:
    "The film could not be saved as a template. Try again in a moment.",
  not_shared:
    "That film is another organisation's. Its scenes can be used once they share it with a review link that allows reuse.",
  question: "Which one?",
  // `{scenes}` is how many; one scene is `author_confirm_one` (sentenceOf)
  author_confirm:
    "Writing {scenes} scenes costs about {fee} credits and takes about {seconds} s. Say yes.",
  author_confirm_one:
    "Writing one scene costs about {fee} credits and takes about {seconds} s. Say yes.",
  tier_instant: "Changing…",
  tier_rebuild: "Rebuilding scene {scene}…",
  tier_author: "Writing scene {scene}… about {seconds} s, {fee} credits",
  preview_failed:
    "The new version could not be prepared. Your ad is as it was, and nothing was charged. Ask again in a moment.",
  stopped: "Stopped. Nothing was changed.",
  interrupted: "The change was interrupted. Your ad is as it was; ask again.",
  // §P29.4 (4): a real person's likeness is declined; a drawn figure is `add_person`
  decline_likeness:
    "I cannot show a real person's likeness. I can add a drawn figure instead — say who they are and what they do.",
  decline_trademark: "I can only use names and marks that are yours.",
  decline_harm: "That is not something TrueReel makes.",
  decline_unclear: "I can only change the film. Say what should change in it.",
  // a change of the brief that does not fit the rest of it (§5.5, P28-T04)
  brief_does_not_fit:
    "That change does not fit the rest of the brief, so the brief was left as it was. Try it another way, or say what you want in the conversation.",
  // the author's step (P22-T04, `author_step`)
  // what the step wrote, by the scenes' numbers (2026-10-22 fix(film) plain counts)
  scene_written_again: "Scene {scene} is written again.",
  scenes_written_again: "Scenes {scenes} are written again.",
  new_scene_written: "A new scene is written.",
  new_scenes_written: "{count} new scenes are written.",
  // the locked scenes a change kept, by their numbers (2026-10-22 fix(copy) scans)
  locked_kept_one: "Scene {scene} is locked and stays as it was.",
  locked_kept: "Scenes {scenes} are locked and stay as they were.",
  kept_changes: "What you changed in scene {scene} before stays: {changes}.",
  changes_not_kept:
    "What you changed in scene {scene} before could not all stay: {changes}. Ask for it again if you want it.",
  task_locked: "This scene is locked. Unlock it before changing it.",
  task_unverified:
    "This task was not verified in the scene and still needs work.",
  activity_sequence_unsupported:
    "This sequence needs separate shots, which I can plan automatically.",
  task_review: "Proposed changes",
  partial_changes: "Some changes were made. Other requests still need work.",
  author_incomplete:
    "These requests still need work. Your film is unchanged; choose how to continue below.",
  author_failed:
    "The scene could not be completed. Your film is unchanged; choose how to continue below.",
  author_geometry:
    "Some positions or camera views could not be made to work. Your film is unchanged.",
  // the first film, written from the brief, could not be (2026-10-23 fix(brief) fit)
  film_not_written:
    "The film could not be made from this brief. Try again, or change a line of the brief.",
  film_unavailable:
    "The film could not be made just now. Try again in a moment.",
  // what a camera change built, when the built scene does not show what the
  // model's words claimed (2026-10-23 fix(camera) product front)
  camera_front:
    "Scene {scene}'s camera now looks at the {object} from the front.",
  camera_side:
    "Scene {scene}'s camera now looks at the {object} from the side.",
  camera_behind:
    "Scene {scene}'s camera now looks at the {object} from behind.",
  author_unavailable:
    "The change could not be made just now. Ask again in a moment.",
  author_rejected:
    "The change could not be made just now. Ask again in a moment.",
  author_refused: "I cannot make that change.",
  author_unchanged:
    "The scene came out as it was, so nothing was charged. Say what should be different.",
  translation_missing:
    "Every caption needs its translation; ask again for the language.",
  translation_cut:
    "A translated caption does not fit the picture; ask again with shorter words.",
  insufficient_credits:
    "Your credits ran out before this was finished. Nothing was changed; the {credits} credits the model used so far were charged.",
  billing_unavailable:
    "Billing is unavailable just now. Ask again in a moment.",
  rate_limited: "That is as many as can be written today. Ask again tomorrow.",
  job_limit: "The day's credit limit would be passed. Nothing was charged.",
  // a thing in hand that cannot be held (fix(things-in-hand) C)
  cannot_hold_that:
    "Nobody holds the {object} in scene {scene}: Objects has no {object} a hand can hold.",
  cannot_hold_suggestion:
    "Choose a {object} in Objects that a hand can hold, so it is used in scene {scene}",
  // a web page on a surface (P26-T02, backend/film/capture.py)
  capture_login:
    "That page is behind a login, so I cannot see it. Upload a screenshot of it instead.",
  capture_unreachable:
    "I could not open that page. Check the address, or upload a screenshot of it instead.",
  // an uploaded picture on a surface (fix(film) upload artwork, backend/film/uploaded.py)
  upload_missing:
    "The picture you chose is no longer in your uploads. Upload it again, or choose another.",
  upload_unreadable:
    "The picture you chose could not be read. Upload it again as a PNG, JPEG or WebP.",
};

/** The words of the template card (P22-T05). */
export const TEMPLATE_CARD = {
  templateTitle: "Template saved",
  templateSlots: "The parts that change:",
  templateCount: "{slots} parts change.",
  templateCountOne: "One part changes.",
  templateOpen: "Make an ad from it",
  close: "Close",
} as const;

/** The say codes of the operations (`ops.py`, P22-T01). */
export const SAY_SENTENCES: Record<string, string> = {
  scene_moved_first: "Scene {scene} now comes first.",
  scene_moved: "Scene {scene} is now scene {to}.",
  carry_became_fade_from:
    "Its carry from scene {came_from} became a fade; say the word to make it a carry again.",
  carry_became_fade:
    "The carry between scene {after} and scene {next} became a fade; say the word to make it a carry again.",
  caption_dropped:
    "A caption in scene {scene} was too short for the new length and was taken out.",
  fell_to_floor:
    "The {object} now stands on the floor in scene {scene}: the new stage has no {surface}.",
  scene_split: "Scene {scene} is now two scenes, cut at {t} s.",
  scenes_joined: "Scenes {first} and {second} are now one scene.",
  scene_deleted: "Scene {scene} is gone.",
  scene_inserted: "A new scene is scene {scene}.",
  artwork_dropped:
    "The new object has nowhere for the artwork on its {surface}; it was left out.",
  pose_dropped: "The new object has no pose '{pose}'; it was left out.",
  surface_chosen: "The {object} has no {asked}, so {surface} changed.",
  light_skipped:
    "In scene {scene} the light cannot change that way, so that part stays as it was.",
  move_skipped:
    "In scene {scene} the camera move cannot change that way, so that part stays as it was.",
  // people (`ops.PEOPLE_SENTENCES`, P29; `test_changes_copy.py` keeps them equal)
  person_added: "{name} is in scene {scene}.",
  action_set: "{name} now plays {action} in scene {scene}.",
  walked: "{name} now walks across scene {scene}.",
  ran: "{name} now runs across scene {scene}.",
  stands: "{name} now stands still in scene {scene}.",
  uses_object: "{name} now {verb} the {object} in scene {scene}.",
  crowd_set: "A crowd of {count} is in scene {scene}.",
  crowd_removed: "Scene {scene} has no crowd now.",
  person_removed: "{name} is out of scene {scene}.",
  person_gone: "{name} is out of the film.",
  pose_not_read: "{name} follows the {what}, so the place was not used.",
  // things in hand (`ops.PEOPLE_SENTENCES`, P30)
  holds: "{name} now holds the {object} in scene {scene}.",
  released: "Nobody holds the {object} in scene {scene} now.",
  picks_up: "{name} now picks up the {object} at {t} s in scene {scene}.",
  puts_down: "{name} now puts the {object} down at {t} s in scene {scene}.",
  gestured: "{name} now {verb} in scene {scene}.",
  gesture_removed: "{name}'s hands are still in scene {scene} now.",
  picked_up_for: "{name} picks up the {object} first.",
  screen_sequence_set:
    "The {object}'s screen now changes {n} times in scene {scene}.",
  screen_sequence_set_once:
    "The {object}'s screen now changes once in scene {scene}.",
  screen_sequence_removed:
    "The {object}'s screen keeps one picture in scene {scene} now.",
  release_on_swap:
    "The new {object} has nowhere to hold it, so nobody holds it now.",
  // 2026-10-24 fix(things-in-hand) ops: a place code chose, a hand carried, a gesture kept
  put_on: "{name} puts the {object} on the {place}, within reach.",
  holds_in:
    "{name} now holds the {object} in the {side} hand in scene {scene}.",
  gesture_moved:
    "{name} now {verb} from {from_t} s to {to_t} s in scene {scene}.",
};

/** What each gesture does (`ops.GESTURE_VERBS`, P30): the `{verb}` of
 *  `gestured`, which the server sends already filled. */
export const GESTURE_VERBS: Record<string, string> = {
  "hold-chest": "holds the {object} to their chest",
  "show-camera": "shows the {object} to the camera",
  "point-remote": "points the remote at the {target}",
  drink: "drinks from the {object}",
  "look-phone": "looks at the {object}",
  "hand-over": "holds out the {object}",
};

/** The cannot codes of the operations (`ops.py`, P22-T01) whose parameters
 *  say all the sentence needs; the others show the server's `sentence`. */
export const CANNOT_SENTENCES: Record<string, string> = {
  locked: "Scene {scene} is locked. Say 'unlock scene {scene}' first.",
  // people (`ops.PEOPLE_SENTENCES`, P29)
  no_anchor: "The {object} has nowhere to {verb}.",
  not_a_figure: "{name} is a person; choose a figure.",
  not_a_pose: "{name} plays actions, not poses: say what they do.",
  person_look_slot: "{name}'s skin and face keep their colour.",
  no_figure: "There is no figure in Objects yet to draw a person with.",
  // a swap to the Object it already is (2026-10-24 fix(things-in-hand) p30c-b, N19)
  same_object: "That is already {object}.",
  same_figure: "{name} is already drawn as that figure.",
  // things in hand (`ops.PEOPLE_SENTENCES`, P30); a validator problem of the same
  // code has no parameters and shows the server's sentence
  no_grip: "The {object} has nowhere to hold it.",
  hand_full: "{name}'s {side} hand is full.",
  grip_hand: "The {object} takes {needs}.",
  held_carrier:
    "Something stands on the {object} or sits on it, so nobody can hold it.",
  not_held: "Nobody holds the {object} in scene {scene}.",
  no_presses: "Nobody points a remote at the {object} in scene {scene}.",
  presses_mismatch:
    "The remote is pressed {n} times; give {pictures} pictures.",
  presses_mismatch_once: "The remote is pressed once; give 2 pictures.",
  no_sequence:
    "The {object}'s screen shows one picture in scene {scene}; say the pictures it changes to.",
  sequence_not_screen: "The {object} has no screen to change.",
  not_a_gesture: "{action} is not something a person does with their hands.",
  gesture_needs_object:
    "{name} {does} with nothing in hand; give them the {thing} first.",
  gesture_grip: "{name} cannot {do} with the {object} held that way.",
  bad_presses:
    "{name} {does}, which presses no button: only pointing the remote does.",
  bad_target: "{name} points the remote at nothing; name the screen.",
  off_stage:
    "The {object} cannot be put down there: scene {scene} has no {surface}.",
  // 2026-10-24 fix(things-in-hand) ops: a real surface in reach, a carried pick-up, room
  // for the other gestures
  put_not_a_surface:
    "The {object} cannot rest on the {surface}: it would float. Put it on the {place} instead, within {name}'s reach.",
  put_out_of_reach:
    "{name} cannot reach the {surface} to put the {object} there. Put it on the {place} instead.",
  put_nowhere:
    "Nothing in scene {scene} is within {name}'s reach to put the {object} on, so it stays in hand.",
  pick_up_no_time:
    "{name} puts the {object} down in scene {before}, so scene {scene} would have to pick it up again, and there is no time before it is used. Put it down at the end of scene {scene} instead.",
  gesture_no_room:
    "{name} also {verb} from {from_t} s to {to_t} s in scene {scene}, and both do not fit there. Say which one to keep.",
  // the validator's (2026-10-22 fix(people) framing)
  person_out_of_view:
    "From that close, a person in the scene would be out of the picture, so the camera stays where it was. The product alone up close can be a scene of its own.",
  no_scene: "Say which scene, or pause the film on it.",
  no_view: "Look around to the view you want first, then say 'from here'.",
  out_of_view:
    "From there the {object} is out of view. Turn toward it and ask again.",
  no_search: "Objects cannot be searched just now. Ask again in a moment.",
  unknown_surface:
    "The {object} has no {surface}; its surfaces are {surfaces}. Say which one.",
  no_fit:
    "Nothing in Objects fits here. I can write the scene again ({fee} credits, about {seconds} s). Say yes.",
  not_neighbours:
    "Scenes {first} and {second} are not next to each other; move one first.",
  different_stage:
    "Scenes {first} and {second} are on different stages, so they cannot be one scene. Say 'put scene {second} on the same stage' first.",
  last_scene: "A film needs at least one scene.",
  no_border: "Scene {scene} is the last scene; nothing comes after it.",
  carry_impossible:
    "The camera cannot carry from scene {after} to scene {next}: they share neither a stage nor an object. A fade would work.",
  unknown_source:
    "I cannot find that film. Say its name as the Ads tab shows it.",
  ...{
    unknown_version: SENTENCES.unknown_version,
    version_not_ready: SENTENCES.version_not_ready,
  },
};

/** The sentence of a superseded change (a new one replaced it while it
 *  waited for an answer or a yes). */
export const SUPERSEDED = "You asked for something else, so this was left.";

/** The sentence with its first letter a capital. */
export function capital(sentence: string): string {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function numberOr(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** One coded sentence in words, or null when the table has none and the
 *  server sent no sentence of its own. */
export function sentenceOf(said: Said): string | null {
  const { code } = said;
  const params = said.params ?? {};
  if (code.startsWith("scope_")) {
    const full = SCOPE_SENTENCES[code];
    return (full ? fillAll(full, params) : null) ?? SCOPE_PLAIN[code] ?? null;
  }
  if (code === "scene_moved") {
    const to = numberOr(params.to);
    return fillAll(
      to === 1 ? SAY_SENTENCES.scene_moved_first : SAY_SENTENCES.scene_moved,
      params,
    );
  }
  if (code === "author_confirm" && numberOr(params.scenes) === 1) {
    return fillAll(SENTENCES.author_confirm_one, params);
  }
  if (code === "carry_became_fade") {
    if (params.came_from !== undefined && params.came_from !== null) {
      return fillAll(SAY_SENTENCES.carry_became_fade_from, params);
    }
    const after = numberOr(params.after);
    return fillAll(SAY_SENTENCES.carry_became_fade, {
      ...params,
      next: after === null ? null : after + 1,
    });
  }
  if (code === "carry_impossible") {
    const after = numberOr(params.after);
    return fillAll(CANNOT_SENTENCES.carry_impossible, {
      ...params,
      next: after === null ? null : after + 1,
    });
  }
  const table =
    SENTENCES[code] ?? SAY_SENTENCES[code] ?? CANNOT_SENTENCES[code] ?? null;
  const filled = table ? fillAll(table, params) : null;
  // a sentence that starts with a name the server sent as it reads inside one ("the
  // woman", 2026-10-24 fix(things-in-hand) ops) starts with a capital
  if (filled) return capital(filled);
  return typeof params.sentence === "string" && params.sentence
    ? params.sentence
    : null;
}

/** What a finished (or waiting) change says, in order: the model's `say`
 *  as written, then each coded sentence, the scope line last (§8.3, §8.5). */
export function answerLines(change: Change): string[] {
  const lines: string[] = [];
  // §5.7: a say in the system's words is never shown (the server drops it first)
  const said = change.say?.trim();
  const say = said && isPlain(said) ? said : undefined;
  // no cost line under an answer (§15.1): the author step's estimate is said
  // only while it waits for the person's yes
  const coded = () =>
    change.says
      .filter(
        (s) =>
          !["author_confirm", "design_confirm"].includes(s.code) ||
          change.state === "waiting_confirm",
      )
      .map(sentenceOf)
      .filter((s): s is string => !!s && s.length > 0);
  switch (change.state) {
    case "applied":
    case "waiting_confirm":
    case "needs_author":
    case "building":
      if (say) lines.push(say);
      lines.push(...coded());
      break;
    case "answered":
      if (say) lines.push(say);
      break;
    case "cannot": {
      if (change.error_code === "no_change") {
        lines.push(say || SENTENCES.no_change);
        break;
      }
      const params = change.error_params ?? {};
      lines.push(
        sentenceOf({ code: change.error_code ?? "change_failed", params }) ??
          SENTENCES.change_failed,
      );
      break;
    }
    case "declined": {
      const own = coded();
      lines.push(
        ...(own.length
          ? own
          : [
              SENTENCES[`decline_${change.decline ?? "unclear"}`] ??
                SENTENCES.decline_unclear,
            ]),
      );
      break;
    }
    case "failed":
      lines.push(
        sentenceOf({
          code: change.error_code ?? "change_failed",
          params: change.error_params ?? {},
        }) ?? SENTENCES.change_failed,
      );
      break;
    case "cancelled":
      lines.push(
        change.error_code === "superseded" ? SUPERSEDED : SENTENCES.stopped,
      );
      break;
    default:
      break;
  }
  return lines;
}

// ------------------------------------------------------------ the phase

/** The scene number a change works on: its scope line's. */
function sceneOf(change: Change): number | null {
  const line = [...change.says]
    .reverse()
    .find((s) => s.code.startsWith("scope_"));
  return numberOr(line?.params?.scene);
}

/** What the box says while a change works (§8.4): Changing…, Rebuilding
 *  scene k… with the share of its scenes ready, Writing scene k… about N s,
 *  F credits. Null when the change does not work. */
export function phaseLine(
  change: Change,
  progress: { fraction: number } | null = null,
): string | null {
  if (change.state === "building") {
    const k = sceneOf(change);
    const line =
      k === null
        ? BOX.rebuildingFilm
        : fillAll(SENTENCES.tier_rebuild, { scene: k });
    const pct =
      progress && progress.fraction < 1
        ? fillAll(BOX.percent, { pct: Math.round(progress.fraction * 100) })
        : null;
    return pct ? `${line} ${pct}` : line;
  }
  if (change.state === "needs_author") {
    if (change.says.some((s) => s.code === "design_working")) {
      return SENTENCES.design_working;
    }
    const confirm = change.says.find((s) => s.code === "author_confirm");
    const params = {
      ...confirm?.params,
      fee: confirm?.params?.fee ?? change.estimate_credits,
    };
    return (
      fillAll(SENTENCES.tier_author, params) ??
      fillAll(BOX.writingNoScene, params) ??
      BOX.writing
    );
  }
  if (change.state === "queued" || change.state === "working") {
    if (change.tier === "rebuild" && change.phase === "applying") {
      const k = sceneOf(change);
      if (k !== null) return fillAll(SENTENCES.tier_rebuild, { scene: k });
    }
    return SENTENCES.tier_instant;
  }
  return null;
}

// ------------------------------------------------------------ the page

export const BOX = {
  title: "Ask for a change",
  hint: "Pause the film, click what you mean or mark an area, and say what should change. You can look around while the film plays.",
  label: "What should change?",
  placeholder: "Make the cap matte black…",
  send: "Send",
  stop: "Stop",
  stopping: "Stopping…",
  rebuildingFilm: "Rebuilding…",
  /** A change sent while the film builds (§6.1). */
  waits: "Waits for the film: it starts when every scene is built.",
  writing: "Writing the scene…",
  writingNoScene: "Writing… about {seconds} s, {fee} credits",
  percent: "({pct} % ready)",
  youSaid: "You",
  film: "TrueReel",
  history: "Changes so far",
  noChanges: "No changes yet. Say the first one.",
  about: "About",
  leaveOut: "Leave out {what}",
  // the question (§8.5)
  questionFrame: "Which one?",
  choose: "Choose this",
  orSay: "Or say it in words",
  answer: "Answer",
  // the author tier (§8.4)
  confirm: "Write it · {fee} credits",
  notNow: "Not now",
  yesWrite: "Yes, write it",
  yesWords: "yes",
  // what goes with the words (§8.2)
  focusChip: "Scene {n}: {name}",
  objectChip: "{name}",
  areaChip: "The marked area",
  viewChip: "Your view",
  spanChip: "{t0}–{t1} s of scene {n}",
  sending: "Sending…",
} as const;

export const SCRUB = {
  scenes: "Scenes",
  play: "Play",
  pause: "Pause",
  time: "{now} / {length}",
  /** The film's name: a tap leaves focus (§7.5). */
  backToFilm: "Back to the film",
  focusOn: "Focus on scene {n}: {name}",
  focused:
    "Scene {n}: {name} — playing alone. Tap again or tap the film's name to leave.",
  /** While the film builds (§6.1): a built scene plays alone. */
  playingAlone: "Scene {n}: {name} — playing alone while the others are built.",
  notBuilt: "Scene {n}: {name} — being built",
  locked: "Scene {n} is locked",
  lockedShort: "Locked",
  seconds: "{s} s",
  moveHint:
    "Drag a scene to move it; or focus it and press Alt with an arrow key.",
  moveText: 'Move scene {n} ("{name}") before scene {m} ("{other}").',
  moveToEnd: 'Move scene {n} ("{name}") to the end.',
  markPart: "Mark a part",
  marking: "Drag along the scene to mark a part",
  clearPart: "Clear the part",
  part: "Marked: {t0}–{t1} s of scene {n}",
  track: "The film's time",
  sceneTrack: "Scene {n}'s time",
} as const;

/** The film while it is written and built (§6.1, §6.2, P28-T09). */
export const BUILD = {
  writing: "Writing the film…",
  building: "Building scene {k} of {n}…",
  hint: "Each scene plays as soon as it is built: pick one in the strip, play it and look around. The whole film plays when every scene is ready.",
  /** The box before version 1 is written (409 `no_version`). */
  boxWriting: "You can ask for a change as soon as the film is written.",
  briefFold: "The brief",
  briefFoldHint:
    "Change a line and only the scenes that read it are written again.",
} as const;

export const LOOK = {
  lookAround: "Look around",
  back: "Back to the film's camera",
  hintPlaying: "Drag to look around.",
  hintPaused: "Click an object or drag to mark an area.",
  hintLooking: "Drag to look around; scroll to come nearer.",
  picture: "The film. {hint}",
  nothingThere: "Nothing to point at there.",
} as const;

export const VERSIONS = {
  title: "Versions",
  version: "Version {n}",
  current: "Playing",
  goBack: "Go back to this version",
  compare: "Compare",
  stopCompare: "Stop comparing",
  undo: "Undo",
  fromVersion: "from version {n}",
  building: "Being made…",
  failed: "Could not be prepared",
  empty: "No versions yet.",
  kinds: {
    first: "made from the brief",
    change: "a change",
    author: "written by TrueReel",
    restore: "back to an earlier version",
    brief: "the brief changed",
  } as Record<string, string>,
  noSay: "No words for this one.",
} as const;

export const COMPARE = {
  title: "Version {n} beside this one",
  this: "This version ({n})",
  other: "Version {n}",
  missing: "Scene “{name}” is not in version {n}.",
  loading: "Loading version {n}…",
  notReady: "Version {n} cannot be played.",
} as const;

export const FILM_STATE = {
  loading: "Loading the film…",
  failed: "The film could not be loaded. Try again in a moment.",
  retry: "Try again",
} as const;

/** The sentence for a change route's error (§25.3, §25.4). */
export const CHANGE_ERRORS: Record<string, string> = {
  no_version: SENTENCES.no_version,
  change_pending:
    "A change is still under way. Wait for it or stop it, then ask again.",
  invalid_state: "That has already been answered.",
  invalid_options: "Choose one of the answers offered.",
  version_not_ready: "That version never played; choose another one.",
  changes_hour:
    "You have asked for many changes in the last hour. Ask again a little later.",
  changes_day:
    "You have asked for the most changes allowed today. Ask again tomorrow.",
  not_found: "That version is not there any more.",
};

export function changeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "rate_limited") {
      const detail = (error.detail ?? {}) as { limit?: unknown };
      if (typeof detail.limit === "string" && CHANGE_ERRORS[detail.limit]) {
        return CHANGE_ERRORS[detail.limit];
      }
    }
    const known = CHANGE_ERRORS[error.code];
    if (known) return known;
  }
  return sentenceFor(error);
}

/** Seconds as the scrubber writes them: one decimal. */
export function secondsText(t: number): string {
  return (Math.round(Math.max(0, t) * 10) / 10).toFixed(1);
}

export const RECOVERY = {
  title: "Ways to continue",
  results: "Task results",
  completed: "Completed",
  unfinished: "Still to do",
  pendingSummary: "Changes remaining: {count}.",
  completedSummary: "Changes completed: {count}.",
  details: "View task details",
  prepareFailed:
    "The new version could not be prepared, so this change is not in your current film.",
  scenes: "Scenes",
  reason:
    "This change could not be completed. Review the task details or edit the request.",
  hint: "Choose a next step, or describe a different approach below.",
  designHint:
    "Designing suitable objects may take longer and cost more. The AI will estimate the extra work; larger changes ask for confirmation.",
  design: "Ask AI to design what is missing",
  retry: "Try the remaining changes again",
  bringCloser: "Bring the resting place closer",
  approachFirst: "Approach before putting it down",
  bringCloserRequest:
    "Adjust the unfinished action: allow moving the object's resting place closer so they can put it down within reach. This replaces the earlier requirement to keep that support in exactly the same position. Keep the other requested outcomes and report the move.",
  approachFirstRequest:
    "Adjust the unfinished action: keep the resting place unchanged, but let them stand and approach it before putting the object down. This replaces the earlier put-down-before-standing order. Keep the other requested outcomes.",
  choose: "Choose objects instead",
  edit: "Edit the request",
  keep: "Keep the current film",
  constraints: "Resolve these constraints:",
  retryRequest:
    "Keep the completed changes. Finish only the unfinished requests below in the named scenes. Explain any remaining blockers and offer choices.",
  designRequest:
    "Keep the completed changes. Finish the unfinished requests below in the named scenes. Create or modify suitable objects if needed; create a new object when the current one cannot be reshaped. Show the additional estimate before starting.",
  reference:
    "Continue the unfinished items from my request {n}, keeping everything already completed.",
} as const;

export const TASK_REVIEW = {
  title: "Proposed changes",
  story: "Scene story",
  editStory: "Edit story",
  done: "Done",
  task: "Task",
  scenes: "Scenes",
  remove: "Remove task",
  add: "Add a task",
  confirm: "Confirm and continue",
  cancel: "Cancel request",
} as const;
